import {useCallback, useMemo} from 'react';
import {FOEDUS_ROLE} from '../domain/constants';

// TODO: Pouziva sa teraz uz na viacerych miestach tak premenovat newOrganOfferFormData na nieco vseobecnejsie
export const useOrganOfferHandlers = ({newOrganOfferFormData, changeSelectedOrganizationsValue, loggedUserPrimaryFoedusRole}) => {
	const getOrganizationsByCountryCode = useCallback(
		(countryCode) =>
			newOrganOfferFormData?.notificationGroups?.organizations?.filter(
				(org) => org?.organization?.code === countryCode
			) || [],
		[newOrganOfferFormData]
	);

	const updateSelectedOrganizations = useCallback(
		(selected, toAdd, toRemove = []) => {
			const selectedSet = new Set(selected.map((org) => org.code));
			toAdd.forEach((org) => selectedSet.add(org.code));
			toRemove.forEach((org) => selectedSet.delete(org.code));
			return newOrganOfferFormData?.notificationGroups?.organizations?.filter(
				(org) => selectedSet.has(org.code)
			);
		},
		[newOrganOfferFormData]
	);

	const onCheckedAllChange = useCallback(
		(event) => {
			changeSelectedOrganizationsValue(
				event?.target?.checked
					? newOrganOfferFormData?.notificationGroups?.organizations
					: []
			);
		},
		[changeSelectedOrganizationsValue, newOrganOfferFormData]
	);

	const onCheckedAllGlobalGroupsChange = useCallback(
		(event) => {
			if (event?.target?.checked) {
				const organizationsToAdd = [];
				newOrganOfferFormData?.notificationGroups?.globalGroups?.forEach(gg => {
					gg?.organizations?.forEach(org => {
						if (!newOrganOfferFormData?.selectedOrganizations?.some(so => so?.code === org?.code)) {
							organizationsToAdd.push(org);
						}
					});
				});
				const newSelectedOrganizations = updateSelectedOrganizations(newOrganOfferFormData?.selectedOrganizations ?? [], organizationsToAdd);
				changeSelectedOrganizationsValue(newSelectedOrganizations);
			} else {
				const organizationsToRemove = [];
				newOrganOfferFormData?.notificationGroups?.globalGroups?.forEach(gg => {
					gg?.organizations?.forEach(org => {
						if (newOrganOfferFormData?.selectedOrganizations?.some(so => so?.code === org?.code)) {
							organizationsToRemove.push(org);
						}
					});
				});
				const newSelectedOrganizations = updateSelectedOrganizations(newOrganOfferFormData?.selectedOrganizations ?? [], [], organizationsToRemove);

				changeSelectedOrganizationsValue(newSelectedOrganizations);
			}
		},
		[changeSelectedOrganizationsValue, newOrganOfferFormData]
	);

	const onCheckedAllPrivateGroupsChange = useCallback(
		(event) => {
			if (event?.target?.checked) {
				const organizationsToAdd = [];
				newOrganOfferFormData?.notificationGroups?.privateGroups?.forEach(gg => {
					gg?.organizations?.forEach(org => {
						if (!newOrganOfferFormData?.selectedOrganizations?.some(so => so?.code === org?.code)) {
							organizationsToAdd.push(org);
						}
					});
				});
				const newSelectedOrganizations = updateSelectedOrganizations(newOrganOfferFormData?.selectedOrganizations ?? [], organizationsToAdd);
				changeSelectedOrganizationsValue(newSelectedOrganizations);
			} else {
				const organizationsToRemove = [];
				newOrganOfferFormData?.notificationGroups?.privateGroups?.forEach(gg => {
					gg?.organizations?.forEach(org => {
						if (newOrganOfferFormData?.selectedOrganizations?.some(so => so?.code === org?.code)) {
							organizationsToRemove.push(org);
						}
					});
				});
				const newSelectedOrganizations = updateSelectedOrganizations(newOrganOfferFormData?.selectedOrganizations ?? [], [], organizationsToRemove);

				changeSelectedOrganizationsValue(newSelectedOrganizations);
			}
		},
		[changeSelectedOrganizationsValue, newOrganOfferFormData]
	);

	const onCountryCheckedChange = useCallback(
		(country, checked) => {
			const countryOrganizations = getOrganizationsByCountryCode(country?.code);
			const selectedOrganizations = newOrganOfferFormData?.selectedOrganizations ?? [];

			const newSelectedOrganizations = checked
				? updateSelectedOrganizations(selectedOrganizations, countryOrganizations)
				: updateSelectedOrganizations(selectedOrganizations, [], countryOrganizations);

			changeSelectedOrganizationsValue(newSelectedOrganizations);
		},
		[changeSelectedOrganizationsValue, getOrganizationsByCountryCode, newOrganOfferFormData, updateSelectedOrganizations]
	);

	const onOrganizationCheckedChange = useCallback(
		(organization, checked) => {
			const selectedOrganizations = newOrganOfferFormData?.selectedOrganizations ?? [];
			const newSelectedOrganizations = checked
				? updateSelectedOrganizations(selectedOrganizations, [organization])
				: updateSelectedOrganizations(selectedOrganizations, [], [organization]);

			changeSelectedOrganizationsValue(newSelectedOrganizations);
		},
		[changeSelectedOrganizationsValue, newOrganOfferFormData, updateSelectedOrganizations]
	);

	const onGlobalGroupCheckedChange = useCallback(
		(globalGroup, checked) => {
			const selectedOrganizations = newOrganOfferFormData?.selectedOrganizations ?? [];
			const groupOrganizations = globalGroup?.organizations || [];

			const newSelectedOrganizations = checked
				? updateSelectedOrganizations(selectedOrganizations, groupOrganizations)
				: updateSelectedOrganizations(selectedOrganizations, [], groupOrganizations);

			changeSelectedOrganizationsValue(newSelectedOrganizations);
		},
		[changeSelectedOrganizationsValue, newOrganOfferFormData, updateSelectedOrganizations]
	);

	const onPrivateGroupCheckedChange = useCallback(
		(privateGroup, checked) => {
			const selectedOrganizations = newOrganOfferFormData?.selectedOrganizations ?? [];
			const groupOrganizations = privateGroup?.organizations || [];

			const newSelectedOrganizations = checked
				? updateSelectedOrganizations(selectedOrganizations, groupOrganizations)
				: updateSelectedOrganizations(selectedOrganizations, [], groupOrganizations);

			changeSelectedOrganizationsValue(newSelectedOrganizations);
		},
		[changeSelectedOrganizationsValue, newOrganOfferFormData, updateSelectedOrganizations]
	);

	const isFoedusAdmin = useMemo(() =>
		loggedUserPrimaryFoedusRole === FOEDUS_ROLE.ADMIN, [loggedUserPrimaryFoedusRole]);

	const getSelectedOrganizationsInCountryCount = (country) =>
		newOrganOfferFormData?.selectedOrganizations?.filter(so => so?.organization?.code === country?.code)?.length || 0;

	const getTotalOrganizationsInCountryCount = (country) =>
		newOrganOfferFormData?.notificationGroups?.organizations?.filter(so => so?.organization?.code === country?.code)?.length;

	const getSelectedOrganizationsInGlobalGroupCount = (globalGroup) =>
		newOrganOfferFormData?.selectedOrganizations?.filter(so => globalGroup?.organizations?.some(org => org?.code === so?.code))?.length || 0;

	const getTotalOrganizationsInGlobalGroupCount = (globalGroup) => globalGroup?.organizations?.length;

	const getSelectedOrganizationsInPrivateGroupCount = (privateGroup) =>
		newOrganOfferFormData?.selectedOrganizations?.filter(so => privateGroup?.organizations?.some(org => org?.code === so?.code))?.length || 0;

	const getTotalOrganizationsInPrivateGroupCount = (privateGroup) => privateGroup?.organizations?.length;

	return {
		onCheckedAllChange,
		onCheckedAllGlobalGroupsChange,
		onCheckedAllPrivateGroupsChange,
		onCountryCheckedChange,
		onOrganizationCheckedChange,
		onGlobalGroupCheckedChange,
		onPrivateGroupCheckedChange,
		isFoedusAdmin,
		getSelectedOrganizationsInCountryCount,
		getTotalOrganizationsInCountryCount,
		getSelectedOrganizationsInGlobalGroupCount,
		getTotalOrganizationsInGlobalGroupCount,
		getSelectedOrganizationsInPrivateGroupCount,
		getTotalOrganizationsInPrivateGroupCount
	};
};
